/**
 * Keeping the backend's copy of this device's push token current
 * (`docs/native_shell.md`, "Push").
 *
 * Two entry points. `syncPushRegistration` runs on every app open once the
 * user is known and only re-registers when the OS has already granted push,
 * because APNs tokens rotate and a stale one delivers nothing. Asking for
 * permission is a product decision that isn't made yet, so it is never done
 * here; `enablePushNotifications` is the explicit action, behind the
 * dev-tools switch on the account page.
 */

import { registerPushToken } from '../api/client'
import {
  pushPermission,
  registerForPush,
  requestPushPermission,
  type PushPermission,
} from './nativeShell'

async function registerAndUpload(): Promise<boolean> {
  const registration = await registerForPush()
  if (!registration) return false
  await registerPushToken({ platform: 'ios', token: registration.token, app_id: registration.appId })
  return true
}

/** Silent, on app open. Resolves true when a token was sent. Never throws. */
export async function syncPushRegistration(): Promise<boolean> {
  try {
    if ((await pushPermission()) !== 'granted') return false
    return await registerAndUpload()
  } catch (err) {
    console.warn('Push registration on open failed:', err)
    return false
  }
}

export interface EnablePushOutcome {
  permission: PushPermission
  /** A token reached the backend. */
  registered: boolean
  /** Why it didn't, when it didn't and the permission wasn't the reason. */
  error?: string
}

/** From an explicit tap: prompt if needed, then register and upload. */
export async function enablePushNotifications(): Promise<EnablePushOutcome> {
  let permission = await pushPermission()
  if (permission === 'unavailable') return { permission, registered: false }
  if (permission === 'prompt') permission = await requestPushPermission()
  if (permission !== 'granted') return { permission, registered: false }
  try {
    return { permission, registered: await registerAndUpload() }
  } catch (err) {
    return { permission, registered: false, error: err instanceof Error ? err.message : String(err) }
  }
}
